import React, { useEffect, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { useHistory } from 'react-router-dom';
import { useDebounce } from 'react-use';
import CustomDialog from 'components/CustomDialog';
import GoogleSignInButton from 'components/GoogleSignInButton';
import Grid from '@mui/material/Unstable_Grid2';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { checkIfEmailExists, clearEmailState } from 'store/slices/email';
import { useAppSelector } from 'hooks/useAppSelector';
import { isTrue, isValidEmail, validate, Validator } from 'utils/Validation';
import { clearAccountCreation, createAccount, saveFirstPassword, validateEmail } from 'store/slices/user';
import SetFirstPasswordForm from 'components/SetFirstPasswordForm';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    FormControl,
    FormControlLabel,
    FormHelperText,
    Link,
    TextField,
    Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ErrorMessage from '../ErrorMessage';
import { MouseEvent } from 'react';

const CODE_LENGTH = 6;

const SimpleSignup = () => {
    const theme = useTheme();
    const dispatch = useAppDispatch();
    const history = useHistory();

    const user = useAppSelector((state) => state.user);
    const emailData = useAppSelector((state) => state.email);
    const auth = useAppSelector((state) => state.auth);
    const isComplete = auth.tokenData?.data?.isComplete;
    const { pathBeforeLoginRequest } = useAppSelector((state) => state.sessionStatus);
    const { creatingAccount, creatingAccountError, creatingAccountSuccess, error } = user;
    const { isEmailAvailable, checkingEmail, checkingEmailSuccess, checkingEmailError } = emailData;
    const map = useAppSelector((state) => state.map);

    useEffect(() => {
        if (auth.userLoginSuccess && isComplete) {
            dispatch(clearAccountCreation());
            dispatch(clearEmailState());
            history.push(pathBeforeLoginRequest ?? '/');
        }
    }, [auth.userLoginSuccess, isComplete]);

    const [email, setEmail] = useState('');
    const [terms, setTerms] = useState(false);
    const [captchaToken, setCaptchaToken] = useState<string | null>(null);

    const [errors, setErrors] = useState({
        email: false,
        emailAvailable: false,
        terms: false,
        captcha: false,
    });
    const [showErrors, setShowErrors] = useState(false);

    // Code verification state
    const [code, setCode] = useState('');
    const [codeError, setCodeError] = useState('');
    const codeInputRef = useRef<HTMLInputElement>(null);

    const validators: Validator<string | boolean>[] = [
        {
            fieldName: 'email',
            fieldValue: email,
            isValid: isValidEmail,
        },
        {
            fieldName: 'emailAvailable',
            fieldValue: email,
            isValid: () => checkingEmailSuccess && isEmailAvailable && !checkingEmailError,
        },
        {
            fieldName: 'terms',
            fieldValue: terms,
            isValid: isTrue,
        },
        {
            fieldName: 'captcha',
            fieldValue: captchaToken,
            isValid: isTrue,
        },
    ];

    useEffect(() => {
        if (!map.isCenterAvailable) {
            history.push('/no-home-selected');
        }
    }, []);

    useEffect(() => {
        validate(validators, setErrors);
    }, [email, terms, captchaToken, isEmailAvailable]);

    useDebounce(
        () => {
            if (isValidEmail(email)) {
                void dispatch(checkIfEmailExists({ email }));
            }
        },
        1000,
        [email]
    );

    // Auto-focus code input when success screen appears
    useEffect(() => {
        if (creatingAccountSuccess && codeInputRef.current) {
            codeInputRef.current.focus();
        }
    }, [creatingAccountSuccess]);

    // Show error from validateEmail rejection
    useEffect(() => {
        if (user.validatingEmailError) {
            setCodeError(user.validatingEmailErrorCont ?? 'Invalid code. Please try again.');
        }
    }, [user.validatingEmailError, user.validatingEmailErrorCont]);

    const closeAction = () => {
        dispatch(clearAccountCreation());
        dispatch(clearEmailState());
        history.push('/');
    };

    const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setEmail(event.target.value);
    };

    const handleCodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const val = event.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, CODE_LENGTH);
        setCode(val);
        setCodeError('');
    };

    const handleVerifyCode = () => {
        if (code.length !== CODE_LENGTH) {
            setCodeError(`Please enter the ${CODE_LENGTH}-character code from your email.`);
            return;
        }
        void dispatch(validateEmail({ hash: code }));
    };

    const availabilityError = showErrors && !errors.email && (errors.emailAvailable || checkingEmail);
    const emailError = showErrors && errors.email;

    const handleLinkClick = (event: MouseEvent<HTMLElement>) => {
        event.preventDefault();
        dispatch(clearAccountCreation());
        dispatch(clearEmailState());
        history.push('/terms-and-conditions');
    };

    const isResent = error === 'EMAIL_UNVERIFIED_RESENT';
    const emailVerified = auth.userLoginSuccess && !isComplete;
    const showCodeEntry = (creatingAccountSuccess || isResent) && !auth.userLoginSuccess;
    const showPasswordSetup = emailVerified;

    return (
        <CustomDialog
            processing={creatingAccount}
            processingCopy={'Creating account...'}
            error={creatingAccountError}
            errorContent={<ErrorMessage specificError={error}/>}
            success={showCodeEntry || showPasswordSetup}
            successContent={
                showPasswordSetup ? (
                    <Box>
                        <Typography variant="subtitle1" gutterBottom>
                            Email verified! Now set your password 🔒
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Choose a password to complete your account setup.
                        </Typography>
                        <SetFirstPasswordForm
                            onSubmit={(password) => void dispatch(saveFirstPassword({ password }))}
                            isProcessing={user.savingPassword}
                            errorMessage={user.savingPasswordErrorCont}
                            submitLabel="Set password"
                        />
                    </Box>
                ) : (
                    <Box>
                        <Typography variant="subtitle1" gutterBottom>
                            {isResent ? 'Check your email' : 'Your account has been created! \uD83C\uDF89'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            We sent a 6-character code to <strong>{email}</strong>. Enter it below, or click the link in the email.
                        </Typography>

                        <TextField
                            inputRef={codeInputRef}
                            fullWidth
                            label="Verification code"
                            value={code}
                            onChange={handleCodeChange}
                            placeholder="e.g. AB3C7Z"
                            inputProps={{
                                maxLength: CODE_LENGTH,
                                style: {
                                    fontFamily: 'monospace',
                                    fontSize: '1.6rem',
                                    letterSpacing: '0.35em',
                                    textAlign: 'center',
                                    textTransform: 'uppercase',
                                },
                            }}
                            error={!!codeError}
                            helperText={codeError || ' '}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleVerifyCode(); }}
                            sx={{ mb: 1 }}
                        />

                        {user.validatingEmailError && (
                            <Alert severity="error" sx={{ mb: 1 }}>
                                {user.validatingEmailErrorCont ?? 'Invalid code. Please try again.'}
                            </Alert>
                        )}

                        <Button
                            fullWidth
                            variant="contained"
                            disabled={user.validatingEmail || code.length !== CODE_LENGTH}
                            onClick={handleVerifyCode}
                            endIcon={user.validatingEmail ? <CircularProgress size={16} color="inherit" /> : null}
                            sx={{ mb: 1 }}
                        >
                            {user.validatingEmail ? 'Verifying...' : 'Verify'}
                        </Button>

                        <Typography variant="caption" color="text.secondary">
                            Didn&apos;t receive the email?{' '}
                            <Link
                                component="button"
                                variant="caption"
                                underline="hover"
                                onClick={closeAction}
                            >
                                Close and try the link instead
                            </Link>
                        </Typography>
                    </Box>
                )
            }
            title={'Sign up'}
            onClose={closeAction}
        >
            {!creatingAccount && !creatingAccountSuccess && !creatingAccountError ? (
                <form aria-autocomplete={'none'}>
                    <Grid>
                        <Grid xs={12}>
                            <GoogleSignInButton />
                        </Grid>
                        <Grid xs={12} sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 1 }}>
                            <hr style={{ flex: 1, border: 'none', borderTop: '1px solid #ccc' }} />
                            <Typography variant="caption" color="text.secondary">or sign up with email</Typography>
                            <hr style={{ flex: 1, border: 'none', borderTop: '1px solid #ccc' }} />
                        </Grid>
                        <Grid xs={12}>
                            <TextField
                                fullWidth
                                label="Email"
                                type="email"
                                value={email}
                                onChange={handleEmailChange}
                                placeholder="Enter email"
                                error={emailError || availabilityError}
                            />
                            <>
                                {checkingEmail || checkingEmailSuccess ? (
                                    !isEmailAvailable ? (
                                        <FormHelperText error>
                                            {checkingEmail ? 'Checking email availability...' : 'The email is in use.'}
                                        </FormHelperText>
                                    ) : (
                                        <FormHelperText sx={{ color: 'success.main' }}>The email is
                                            available</FormHelperText>
                                    )
                                ) : null}
                                {emailError ?
                                    <FormHelperText error>You need to enter a valid email.</FormHelperText> : null}
                            </>
                        </Grid>
                        <Grid xs={12}>
                            <FormControl required error={showErrors && errors.terms}>
                                <FormControlLabel
                                    control={
                                        <Checkbox id="terms" checked={terms}
                                            onChange={(event) => setTerms(event.target.checked)}
                                        />
                                    }
                                    label={
                                        <Typography>
                                            By checking this box, I acknowledge and agree to the{' '}
                                            <Link component="button" onClick={handleLinkClick} sx={{ fontSize: '1rem' }}>
                                                terms and conditions
                                            </Link>
                                        </Typography>
                                    }
                                />
                            </FormControl>
                            {showErrors && errors.terms && (
                                <FormHelperText error>You need to accept the terms and conditions to
                                    join.</FormHelperText>
                            )}
                        </Grid>
                        <Grid
                            xs={12}
                            sx={{
                                marginTop: theme.spacing(3),
                            }}
                        >
                            <div>
                                <ReCAPTCHA
                                    sitekey={process.env.REACT_APP_CAPTCHA_SITE_KEY}
                                    onChange={(value: string) => {
                                        setCaptchaToken(value);
                                    }}
                                />
                            </div>
                        </Grid>
                        {showErrors && errors.captcha &&
                            <FormHelperText error>You need to prove you're human!</FormHelperText>}
                        <Box sx={{ width: '100%', textAlign: 'center', pt: 2 }}>
                            <Button
                                variant="contained"
                                disabled={creatingAccount || checkingEmail}
                                onClick={() => {
                                    if (!creatingAccount && !checkingEmail) {
                                        if (validate(validators, setErrors)) {
                                            setCaptchaToken(null);
                                            void dispatch(createAccount({
                                                email,
                                                terms,
                                                captchaToken,
                                                isSimple: true
                                            }));
                                        } else {
                                            setShowErrors(true);
                                        }
                                    }
                                }}
                            >
                                Sign up
                            </Button>
                            {!creatingAccountSuccess && !creatingAccountError && (
                                <Box sx={{ mt: 2 }}>
                                    <Typography variant='body2' color='text.secondary'>
                                        Already have an account?{' '}
                                        <Link color='secondary' href='/sign-in' underline='hover'>
                                            Sign in
                                        </Link>
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Grid>
                </form>
            ) : null}
        </CustomDialog>
    );
};

export default SimpleSignup;
